import { usePremiumOffer } from './PremiumOfferProvider';
import { useAppSelector } from '@src/state/hooks';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CrownIcon } from '@src/components/icons/CrownIcon';
import { isDotCom } from '@src/site';
import './PremiumTierPicker.css';

type Tier = { code: string; amountMinor: number; currency: string };
export function PremiumTierPicker(props: { disabled?: boolean }) {
  return <PremiumTierPickerContent key={isDotCom() ? 'com' : 'ru'} {...props} />;
}
function PremiumTierPickerContent({ disabled = false }: { disabled?: boolean }) {
  const { variant } = usePremiumOffer();
  const { i18n } = useTranslation();
  const user = useAppSelector(state => state.session.user);
  const ru = i18n.language.startsWith('ru');
  const [params, setParams] = useSearchParams();
  const [tiers, setTiers] = useState<Tier[]>([]);
  const [failed, setFailed] = useState(false);
  const [retry, setRetry] = useState(0);
  const currency = isDotCom() ? 'USD' : 'RUB';
  const selected = ['tier_1', 'tier_2', 'tier_3'].includes(params.get('tier') || '') ? params.get('tier') : 'tier_2';
  useEffect(() => {
    const controller = new AbortController();
    fetch('/api/premium/tiers', { signal: controller.signal, credentials: 'same-origin' })
      .then(async response => {
        if (!response.ok) throw new Error('Unavailable');
        const data: Tier[] = await response.json();
        if (!Array.isArray(data) || data.length !== 3 || data.some((tier, i) => tier.code !== `tier_${i + 1}` || tier.currency !== currency || !Number.isSafeInteger(tier.amountMinor) || tier.amountMinor <= 0)) throw new Error('Invalid tiers');
        if (!controller.signal.aborted) setTiers(data);
      }).catch(() => { if (!controller.signal.aborted) setFailed(true); });
    return () => controller.abort();
  }, [currency, retry, variant]);
  const names = ru ? ['Волшебник', 'Санта-Мастер', 'Санта-Магистр'] : ['Wizard', 'Santa Master', 'Santa Grandmaster'];
  return <div className="premium-tier-picker">
    {failed ? <p role="alert">{ru ? 'Не удалось загрузить тарифы.' : 'Could not load plans.'} <button onClick={() => { setFailed(false); setRetry(value => value + 1); }}>{ru ? 'Повторить' : 'Retry'}</button></p> : !tiers.length ? <p role="status">{ru ? 'Загружаем тарифы…' : 'Loading plans…'}</p> : <fieldset disabled={disabled}>
      <legend>{ru ? 'Выберите свой тариф' : 'Choose your plan'}</legend>
      <div className="premium-tier-picker__cards">{tiers.map((tier, index) => {
        const access = user?.premium?.offers.find(item => item.tier === tier.code);
        const amount = access?.amountDueMinor ?? tier.amountMinor;
        const locked = access && ['owned', 'included', 'unavailable'].includes(access.status);
        return <label key={tier.code} className={`premium-tier-picker__card${selected === tier.code ? ' is-selected' : ''}`}>
        <input type="radio" name="premium-tier" disabled={user?.premium?.offers.some(item => item.tier === tier.code && ['owned', 'included', 'unavailable'].includes(item.status))} value={tier.code} checked={selected === tier.code} onChange={() => {
          const next = new URLSearchParams(params); next.set('tier', tier.code); next.delete('result'); setParams(next, { replace: true });
        }} />
        <span className="premium-tier-picker__crown" aria-hidden="true"><CrownIcon /></span>
        <span className="premium-tier-picker__name">{names[index]}</span>
        <strong>{new Intl.NumberFormat(i18n.language, { style: 'currency', currency, minimumFractionDigits: amount % 100 ? 2 : 0 }).format(amount / 100)}</strong>
        <span className="premium-tier-picker__selected">{locked ? (access.status === 'owned' ? (ru ? 'Ваш текущий тариф' : 'Your current plan') : (ru ? 'Включено' : 'Included')) : selected === tier.code ? (ru ? '✓ Выбрано' : '✓ Selected') : (ru ? 'Выбрать' : 'Select')}</span>
      </label>; })}</div>
    </fieldset>}
    {disabled && <p>{ru ? 'Открываем оплату…' : 'Opening checkout…'}</p>}
  </div>;
}

export function PremiumTierDescription({ tier }: { tier: string }) {
  const { i18n } = useTranslation();
  const ru = i18n.language.startsWith('ru');
  const descriptions = ru ? {
    tier_1: isDotCom() ? 'Доступ ко всем текущим\nи будущим темам оформления.' : 'Все темы оформления. Без рекламы для вас\nна всём сайте и для участников ваших игр.',
    tier_2: 'Всё из тарифа «Волшебник», плюс расширенные инструменты организатора.',
    tier_3: 'Всё из тарифа «Мастер», плюс свой адрес игры и несколько организаторов.',
  } : {
    tier_1: isDotCom() ? 'Access to all current\nand future themes.' : 'All themes. No ads for you across the site\nor for anyone in your games.',
    tier_2: 'Everything in Wizard, plus advanced organizer tools.',
    tier_3: 'Everything in Master, plus a custom game address and multiple organizers.',
  };
  return <p className="premium-buy__tier-description">
    {Object.entries(descriptions).map(([key, description]) =>
      <span key={key} className="premium-buy__tier-description-sizer" aria-hidden="true">{description}</span>
    )}
    <span aria-live="polite" aria-atomic="true">{descriptions[tier as keyof typeof descriptions] || descriptions.tier_2}</span>
  </p>;
}
